import { useEffect, useState, useCallback } from "react";
import { Activity, Loader2, Filter, X } from "lucide-react";
import { api } from "@/lib/api";
import { formatLocalDateTime } from "@/lib/dates";

const ROLE_COLOR = {
  admin: "text-primary-orange", plant_manager: "text-sky-400", dispatcher: "text-emerald-400",
  batcher: "text-amber-400", loader: "text-amber-400", driver: "text-zinc-300",
  accounts: "text-purple-400", contractor: "text-cyan-400",
};

// Friendly device/browser summary from a raw user-agent string.
function parseDevice(ua) {
  if (!ua) return "—";
  let browser = "Unknown browser";
  if (/Edg\//.test(ua)) browser = "Edge";
  else if (/OPR\/|Opera/.test(ua)) browser = "Opera";
  else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) browser = "Chrome";
  else if (/Firefox\//.test(ua)) browser = "Firefox";
  else if (/Safari\//.test(ua) && /Version\//.test(ua)) browser = "Safari";
  let os = "";
  if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";
  else if (/Android/.test(ua)) os = "Android";
  else if (/Windows NT/.test(ua)) os = "Windows";
  else if (/Mac OS X/.test(ua)) os = "macOS";
  else if (/Linux/.test(ua)) os = "Linux";
  return os ? `${browser} · ${os}` : browser;
}

export default function SuperAdminLoginActivity({ orgs = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orgId, setOrgId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (orgId) params.set("org_id", orgId);
    if (from) params.set("date_from", from);
    if (to) params.set("date_to", to);
    params.set("limit", "500");
    api.get(`/superadmin/login-activity?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ logins: [], count: 0 }))
      .finally(() => setLoading(false));
  }, [orgId, from, to]);

  useEffect(() => { load(); }, [load]);

  const clear = () => { setOrgId(""); setFrom(""); setTo(""); };
  const hasFilter = orgId || from || to;
  const logins = data?.logins || [];

  return (
    <div className="space-y-4" data-testid="sa-login-activity">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <span className="label-tag flex items-center gap-1 text-[9px]"><Filter className="w-3 h-3" /> Organisation</span>
          <select data-testid="sa-la-org-filter" value={orgId} onChange={(e) => setOrgId(e.target.value)}
            className="h-9 min-w-[12rem] rounded-none bg-transparent border border-industrial px-2 text-sm">
            <option value="">All organisations</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="label-tag text-[9px]">From</span>
          <input data-testid="sa-la-date-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="h-9 rounded-none bg-transparent border border-industrial px-2 text-sm" />
        </div>
        <div className="flex flex-col gap-1">
          <span className="label-tag text-[9px]">To</span>
          <input data-testid="sa-la-date-to" type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="h-9 rounded-none bg-transparent border border-industrial px-2 text-sm" />
        </div>
        {hasFilter && (
          <button data-testid="sa-la-clear" onClick={clear}
            className="h-9 px-3 inline-flex items-center gap-1 border border-industrial text-xs uppercase tracking-wide hover:bg-surface">
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        )}
        <div className="ml-auto mono text-[11px] text-muted-industrial" data-testid="sa-la-count">
          {loading ? "loading…" : `${logins.length} sign-in${logins.length === 1 ? "" : "s"}`}
        </div>
      </div>

      <div className="border border-industrial max-h-[520px] overflow-y-auto">
        <table className="w-full text-left" data-testid="sa-la-table">
          <thead className="sticky top-0 bg-surface-2 z-10">
            <tr className="mono text-[9px] uppercase tracking-widest text-muted-industrial">
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Organisation</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">IP</th>
              <th className="px-3 py-2">Device</th>
            </tr>
          </thead>
          <tbody>
            {loading && !data ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-xs text-muted-industrial">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> loading…
              </td></tr>
            ) : logins.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-xs text-muted-industrial">
                <Activity className="w-4 h-4 inline mr-2" /> No sign-ins match these filters.
              </td></tr>
            ) : logins.map((l) => (
              <tr key={l.id} className="border-t border-industrial/60" data-testid={`sa-la-row-${l.id}`}>
                <td className="px-3 py-2 mono text-xs text-ink">{l.name || "—"}</td>
                <td className="px-3 py-2 mono text-[11px] text-muted-industrial">{l.email}</td>
                <td className="px-3 py-2 mono text-[11px] text-ink">{l.org_name || "—"}</td>
                <td className={`px-3 py-2 mono text-[10px] uppercase tracking-widest ${ROLE_COLOR[l.role] || "text-muted-industrial"}`}>{(l.role || "").replace("_", " ")}</td>
                <td className="px-3 py-2 mono text-[11px] text-muted-industrial whitespace-nowrap">{formatLocalDateTime(l.at)}</td>
                <td className="px-3 py-2 mono text-[11px] text-muted-industrial">{l.ip || "—"}</td>
                <td className="px-3 py-2 mono text-[11px] text-muted-industrial" title={l.user_agent || ""}>{parseDevice(l.user_agent)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
